import type { ToolResultItem } from '../components/prioritization/ToolWrapper';
import type { AggregateResult, ToolOutputType } from '../types/prioritization/toolSession';
import { sortAggregatesForDisplay } from './liveSessionDisplay';
import { findTies, type TieGroups } from './sessions/rankTies';

function extractNumericMetadata(metadata?: Record<string, unknown>): Record<string, number> {
  if (!metadata) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(metadata).filter((entry): entry is [string, number] => {
      const [, value] = entry;
      return typeof value === 'number' && Number.isFinite(value);
    })
  );
}

export function buildSessionScoreRecord(
  result: ToolResultItem,
  totalItems: number
): Record<string, number> {
  const fallbackTotal = totalItems - ((result.rank ?? totalItems) - 1);

  return {
    total: result.score ?? fallbackTotal,
    ...extractNumericMetadata(result.metadata),
    ...(result.scoreBreakdown ?? {}),
  };
}

export function buildSessionScores(results: ToolResultItem[]): Record<string, Record<string, number>> {
  return Object.fromEntries(
    results.map((result) => [result.id, buildSessionScoreRecord(result, results.length)])
  );
}

export function buildSessionClassifications(
  results: ToolResultItem[]
): Record<string, Record<string, string>> {
  return Object.fromEntries(
    results.map((result) => [
      result.id,
      {
        ...(result.category ? { category: result.category } : {}),
        ...(result.classificationBreakdown ?? {}),
      },
    ])
  );
}

/** What `run_session` saves for a sitting with many people's answers. */
export interface RankingOutputFromResponses {
  toolId: string;
  toolName: string;
  toolConfig: Record<string, unknown>;
  rankedItemIds: string[];
  scores: Record<string, { total: number }>;
  normalizedScores: Record<string, number>;
  /** `primaryCategory` is what `run_session` writes to session_results.classification. */
  classifications: Record<
    string,
    { primaryCategory: string; agreement: number; breakdown: Record<string, number> }
  >;
  tiers: Record<string, string>;
  /** Items with the same total share a rank (S-5); none for a sorting method. */
  ties: TieGroups;
  overrides: [];
  generatedAt: string;
  lastModifiedAt: string;
  aggregatedFrom: number;
}

/**
 * The one way a sitting's answers become its saved result — the app's Close,
 * the session page's Close and Aggregate, and the live board's Close & save all
 * come here, so the same answers always give the same order. The order is the
 * live board's (`sortAggregatesForDisplay`), so what the room watched is what
 * gets saved.
 */
export function buildRankingOutputFromAggregates(
  aggregates: AggregateResult[],
  outputType: ToolOutputType,
  meta: { toolId: string; toolName: string; responseCount: number }
): RankingOutputFromResponses {
  const sorted = sortAggregatesForDisplay(aggregates, outputType);

  const scores: RankingOutputFromResponses['scores'] = {};
  for (const agg of sorted) {
    const value =
      outputType === 'ranking' ? agg.totalScore ?? agg.averageScore : agg.averageScore;
    if (value !== undefined) scores[agg.itemId] = { total: value };
  }

  const max = Math.max(0, ...Object.values(scores).map((s) => s.total));
  const normalizedScores: Record<string, number> = {};
  if (max > 0) {
    for (const [id, s] of Object.entries(scores)) {
      normalizedScores[id] = Math.round((s.total / max) * 100);
    }
  }

  const classifications: RankingOutputFromResponses['classifications'] = {};
  const tiers: Record<string, string> = {};
  for (const agg of sorted) {
    if (!agg.majorityCategory || !agg.categoryVotes) continue;
    classifications[agg.itemId] = {
      primaryCategory: agg.majorityCategory,
      agreement: Math.round((agg.consensusStrength ?? 0) * 100),
      breakdown: agg.categoryVotes,
    };
    tiers[agg.itemId] = agg.majorityCategory;
  }

  const rankedItemIds = sorted.map((a) => a.itemId);
  const now = new Date().toISOString();
  return {
    toolId: meta.toolId,
    toolName: meta.toolName,
    toolConfig: {},
    rankedItemIds,
    scores,
    normalizedScores,
    classifications,
    tiers,
    ties: outputType === 'classification' ? [] : findTies(rankedItemIds, scores),
    overrides: [],
    generatedAt: now,
    lastModifiedAt: now,
    aggregatedFrom: meta.responseCount,
  };
}
